import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import type { Socket } from 'socket.io-client';
import { io } from 'socket.io-client';

import { useAuthSafe } from '@adflow/frontend/util-auth';
import type {
	ClientToServerEvents,
	ServerToClientEvents,
} from '@adflow/shared/util-websockets';

type ClientSideSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

const WebsocketClientContext = createContext<ClientSideSocket | null>(null);

interface WebsocketProviderProps {
	children: ReactNode;
}

export function WebsocketProvider({ children }: WebsocketProviderProps) {
	const { user } = useAuthSafe();

	const [wsClient, setWSClient] = useState<ClientSideSocket | null>(null);

	useEffect(() => {
		const client = io({
			transports: ['websocket'],
			reconnectionDelayMax: 10_000,
		});
		setWSClient(client);
		return () => {
			client.disconnect();
			setWSClient(null);
		};
	}, [user]);

	return (
		<WebsocketClientContext.Provider value={wsClient}>
			{children}
		</WebsocketClientContext.Provider>
	);
}

/**
 * Get the websocket client. If you want to listen to events use
 * `useWebsocketListener` instead.
 */
export const useWebsockets = () => useContext(WebsocketClientContext);
